import { DatePipe } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { ViewActivityDialogComponent } from '@context/frontend/activity';
import { AlertService } from '@context/frontend/alert';
import { DialogService, IsExpiredPipe } from '@context/frontend/common';
import { getUaDevice, IsDevicePipe } from '@context/frontend/native/platform';
import {
	AcknowledgeDialogComponent,
	DetailMenuDialogComponent,
	DetailMenuDropdownComponent,
} from '@context/frontend/ui/components';
import { Chemical, ChemicalRevision } from '@context/shared/types/chemical';
import { DetailMenuItem, isExpired } from '@context/shared/types/common';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiButton, TuiDropdown, TuiHint, TuiIcon } from '@taiga-ui/core';
import { TuiBadge } from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { DocumentReference } from 'firebase/firestore';
import { ChemicalService } from '../../services';
import { ChemicalThumbnailComponent } from '../chemical-thumbnail/chemical-thumbnail.component';

@Component({
	imports: [
		TranslocoModule,
		TuiIcon,
		ChemicalThumbnailComponent,
		IsExpiredPipe,
		DatePipe,
		TuiButton,
		DetailMenuDropdownComponent,
		TuiDropdown,
		IsDevicePipe,
		TuiHint,
		TuiBadge,
	],
	selector: 'ctx-chemical-revision-row',
	templateUrl: 'chemical-revision-row.component.html',
	styleUrl: 'chemical-revision-row.component.scss',
	host: {
		'[attr.device]': 'device',
	},
})
export class ChemicalRevisionRowComponent {
	readonly device = getUaDevice();

	readonly revision = input.required<ChemicalRevision>();
	readonly revisionRef =
		input.required<DocumentReference<ChemicalRevision>>();
	readonly chemical = input.required<Chemical>();

	readonly update = output<void>();

	menuOpen = false;
	readonly menuItems = signal<DetailMenuItem[]>([]);
	readonly busy = signal(false);

	private readonly dialog = inject(DialogService);
	private readonly chemicalService = inject(ChemicalService);
	private readonly alert = inject(AlertService);

	readonly isLatestRevision = computed(() => {
		const chemical = this.chemical();
		const revisionRef = this.revisionRef();
		return (
			chemical.latestRevision &&
			(chemical.latestRevision as DocumentReference<ChemicalRevision>)
				.id === revisionRef.id
		);
	});

	readonly revisionActivityDialog = () =>
		this.dialog.open(
			new PolymorpheusComponent(ViewActivityDialogComponent),
			ViewActivityDialogComponent.Options({
				data: { entityRef: this.revisionRef() },
			}),
		);

	readonly revisionMenuDialog = () =>
		this.dialog.open(
			new PolymorpheusComponent(DetailMenuDialogComponent),
			DetailMenuDialogComponent.Options({
				label: 'chemical.manage-revision',
				data: { menuItems: this.menuItems() },
			}),
		);

	constructor() {
		effect(() => {
			const revision = this.revision();
			this.setupMenuItems(revision);
		});
	}

	setupMenuItems(revision: ChemicalRevision) {
		const menuItems: DetailMenuItem[] = [
			{
				text: 'action.view-history',
				iconStart: '@tui.history',
				handler: () => this.onViewHistory(),
			},
		];

		if (!isExpired(revision.expiresAt)) {
			menuItems.push({
				text: 'chemical.expire-revision',
				iconStart: '@tui.calendar-x',
				handler: () => this.onExpireNow(),
			});
		}

		this.menuItems.set(menuItems);
	}

	readonly acknowledgeDialog = (
		label: string,
		description: string,
		acknowledge: string,
	) =>
		this.dialog.open(
			new PolymorpheusComponent(AcknowledgeDialogComponent),
			AcknowledgeDialogComponent.Options({
				label,
				data: {
					description,
					acknowledge,
					translationParams: {
						regulationNumber: this.revision().regulationNumber,
					},
				},
			}),
		);

	readonly revisionExpireDialog = () =>
		this.acknowledgeDialog(
			'chemical.expire-revision-header',
			'chemical.expire-revision-description',
			'chemical.expire-revision-acknowledge',
		);

	readonly onViewHistory = () => this.revisionActivityDialog().subscribe();

	readonly onMenuOpen = () => this.revisionMenuDialog().subscribe();

	readonly onExpireNow = () => {
		if (this.busy()) return;

		this.revisionExpireDialog().subscribe({
			next: (data) => {
				if (!data) return;

				this.busy.set(true);
				this.chemicalService
					.expireRevision(this.revisionRef())
					.then(() => {
						this.alert.open('generic-success').subscribe();
						this.update.emit();
					})
					.catch((error) => {
						console.error(
							'There was an issue expiring the revision',
							error,
						);
						this.alert.open('generic-error').subscribe();
					})
					.finally(() => {
						this.busy.set(false);
					});
			},
		});
	};
}
